import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card } from "react-bootstrap";
import { getData } from "../api";
import { blogData } from "../utils/blogData";
import ScrollToTopButton from "./ScrollToTopButton";
import blogDefaultImg from "../assets/images/blog-default.png";

const fallbackBlogs = [];

let imageUrl = "https://www.centillionsoftechhrm.com/api/v1/resources/blogs/";
const normalizeBlog = (blog) => {
  const id = blog?._id || blog?.id || blog?.blogId || blog?.slug;
  const content =
    blog?.content ||
    blog?.description ||
    `<p>${blog?.excerpt || blog?.summary || "Read the latest insights from Centillion Softech."}</p>`;

  return {
    id,
    title: blog?.title || blog?.name || "Untitled blog",
    author: blog?.author || blog?.createdBy || "Centillion Softech",
    image:
      blog?.image || blog?.coverImage || blog?.featuredImage || blogDefaultImg,
    date:
      blog?.date ||
      (blog?.createdAt
        ? new Date(blog.createdAt).toLocaleDateString()
        : "Recent") ||
      "Recent",
    content,
  };
};

const BlogDetailsPage = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const result = await getData(
          `${process.env.REACT_APP_BASEURL}/blog/get/${id}`,
        );
        const apiBlog = result?.data?.data || result?.data || null;

        if (apiBlog) {
          setBlog(normalizeBlog(apiBlog));
        } else {
          const foundBlog = fallbackBlogs.find(
            (item) => String(item.id) === String(id),
          );
          setBlog(foundBlog || null);
        }
      } catch (error) {
        const foundBlog = fallbackBlogs.find(
          (item) => String(item.id) === String(id),
        );
        setBlog(foundBlog || null);
      }
    };

    fetchBlog();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchRecentBlogs = async () => {
      try {
        const result = await getData(
          `${process.env.REACT_APP_BASEURL}/blog/get`,
        );
        const apiBlogs = result?.data?.data || result?.data || fallbackBlogs;
        const blogList = Array.isArray(apiBlogs)
          ? apiBlogs.map(normalizeBlog)
          : fallbackBlogs.map(normalizeBlog);

        const filtered = blogList
          .filter((item) => String(item.id) !== String(id))
          .sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA;
          })
          .slice(0, 5);

        setRecentBlogs(filtered.length ? filtered : fallbackBlogs.slice(0, 5));
      } catch (error) {
        const fallbackRecent = fallbackBlogs
          .filter((item) => String(item.id) !== String(id))
          .slice(0, 5);
        setRecentBlogs(fallbackRecent);
      }
    };

    fetchRecentBlogs();
  }, [id]);

  if (!blog) {
    return (
      <div className="py-5 mt-5 text-center">
        <Container>
          <h2>Blog Not Found</h2>
          <Link to="/blog" className="btn btn-primary mt-3">
            Back to Blogs
          </Link>
        </Container>
      </div>
    );
  }

  return (
    <>
      <div className="mt-header">
        <section className="blog-details-section py-5">
          <Container>
            <Row className="mb-4">
              <Col lg={8}>
                <h1 className="display-5 fw-bold mb-3">{blog.title}</h1>
                <div className="d-flex align-items-center mb-4 text-muted">
                  <i className="fa-solid fa-calendar-alt me-2 text-primary"></i>
                  <span>{blog.date}</span>
                  <span className="mx-3 text-light">|</span>
                  <i className="fa-solid fa-user me-2 text-primary"></i>
                  <span>By {blog.author}</span>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={8}>
                <div className="blog-content-wrapper bg-white p-4 p-md-5 shadow-sm rounded">
                  <img
                    src={blog.image ? imageUrl + blog.image : blogDefaultImg}
                    alt={blog.title}
                    className="img-fluid rounded mb-4 w-100 shadow"
                    style={{ maxHeight: "500px", objectFit: "cover" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = blogDefaultImg;
                    }}
                  />

                  <div
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                  />
                  <hr className="my-5" />

                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <div className="share-post d-flex align-items-center">
                      <span className="fw-bold me-3">Share this post:</span>
                      <a
                        href="#"
                        className="rounded-circle d-flex align-items-center justify-content-center me-2 shadow-sm text-decoration-none transition-transform hover-scale"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#0080c5",
                          color: "#fff",
                        }}
                      >
                        <i className="fa-brands fa-facebook-f"></i>
                      </a>
                      <a
                        href="#"
                        className="rounded-circle d-flex align-items-center justify-content-center me-2 shadow-sm text-decoration-none transition-transform hover-scale"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#0080c5",
                          color: "#fff",
                        }}
                      >
                        <i className="fa-brands fa-twitter"></i>
                      </a>
                      <a
                        href="#"
                        className="rounded-circle d-flex align-items-center justify-content-center shadow-sm text-decoration-none transition-transform hover-scale"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#0080c5",
                          color: "#fff",
                        }}
                      >
                        <i className="fa-brands fa-linkedin-in"></i>
                      </a>
                    </div>
                    <Link
                      to="/blog"
                      className="btn btn-outline-primary rounded-pill px-4"
                    >
                      <i className="fa-solid fa-arrow-left me-2"></i> Back to
                      Blogs
                    </Link>
                  </div>
                </div>
              </Col>

              <Col lg={4}>
                <div className="blog-sidebar mt-5 mt-lg-0">
                  <Card className="border-0 shadow-sm mb-4">
                    <Card.Body>
                      <h5 className="fw-bold mb-3 border-bottom pb-2">
                        Recent Posts
                      </h5>
                      {recentBlogs.map((recent) => (
                        <div
                          key={recent.id}
                          className="d-flex mb-3 align-items-center"
                        >
                          <img
                            src={recent.image || blogDefaultImg}
                            alt={recent.title}
                            className="rounded"
                            style={{
                              width: "80px",
                              height: "60px",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = blogDefaultImg;
                            }}
                          />
                          <div className="ms-3">
                            <Link
                              to={`/blog-details/${recent.id}`}
                              className="text-dark text-decoration-none small fw-bold d-block mb-1 line-clamp-2"
                            >
                              {recent.title}
                            </Link>
                            <span className="text-muted small">
                              {recent.date}
                            </span>
                          </div>
                        </div>
                      ))}
                    </Card.Body>
                  </Card>

                  <Card className="border-0 shadow-sm secondary-content bg-primary text-white text-center p-4">
                    <Card.Body>
                      <h4 className="fw-bold mb-3">Have a project in mind?</h4>
                      <p className="mb-4">
                        Let's build something amazing together.
                      </p>
                      <Link
                        to="/contactus"
                        className="btn btn-light rounded-pill px-4 fw-bold"
                      >
                        Contact Us
                      </Link>
                    </Card.Body>
                  </Card>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </div>

      <ScrollToTopButton />
    </>
  );
};

export default BlogDetailsPage;
